import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { storeWriteSettingsAdapts, StoreWriteSettingsAdapter } from './store-write-settings.adapter';
import { CopySink } from 'app/model/pipeline/copysink/copysink';
import { LakehouseWriteSettings } from 'app/model/pipeline/copysink/store-write-settings/lakehouse-store-write-settings';
import { DeltaDataset, FormatDatasetV2, Owned, StoreSettingPartitionKey } from 'shell-models-lib';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import type { CopyActivity } from 'app/model/pipeline/copy.activity';
import { CopyJobActivity } from 'app/model/copyjob/copy-job.activity';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(LakehouseWriteSettings),
    asynchronous: true,
    adapts: {
        ...storeWriteSettingsAdapts,
        blockSizeInMB: '*',
        metadata: '*',
        partitionOption: '*',
        partitionNameList: '*',
        tableActionOption: '*'
    }
})
export class LakehouseWriteSettingsAdapter extends StoreWriteSettingsAdapter<
    LakehouseWriteSettings,
    Model.StoreWriteSettings
> {
    protected override async _toResource(model: LakehouseWriteSettings): Promise<Model.StoreWriteSettings> {
        const result: Model.StoreWriteSettings = await super._toResource(model);
        StoreWriteSettingsAdapter.toStoreWriteSettingsResource(model, result);
        super._commonPropsToResource(result, model);
        const sinkDataset = (model.owner?.owner as CopyActivity)?.sinkDataset as FormatDatasetV2;
        if (
            // Legacy Lakehouse table path based on Delta format. Skip it for Copy job which supports modern Delta format
            !(model.owner?.owner instanceof CopyJobActivity) &&
            sinkDataset instanceof DeltaDataset &&
            sinkDataset.rootFolder === 'Tables'
        ) {
            this._lhTablePropsToResource(result, model);
            Reflect.deleteProperty(result, 'copyBehavior');
        }
        return result;
    }

    protected override async _toModel(
        resource: Model.StoreWriteSettings,
        copySink: CopySink
    ): Promise<LakehouseWriteSettings> {
        const result = await super._toModel(resource, copySink);
        StoreWriteSettingsAdapter.toStoreWriteSettingsModel(resource, result);
        super._commonPropsToModel(resource, result, copySink);
        this._lhTablePropsToModel(resource, result, copySink);
        return result;
    }

    protected _lhTablePropsToResource(result: Model.StoreWriteSettings, model: LakehouseWriteSettings): void {
        result.tableActionOption = ValueAdapter.toResource(model, LakehouseWriteSettings.TABLE_ACTION) || 'Append';
        if (result.tableActionOption !== 'Append') {
            if (ValueAdapter.isTemplated(model, LakehouseWriteSettings.PARTITION_OPTION)) {
                result.partitionOption = ValueAdapter.toResource(model, LakehouseWriteSettings.PARTITION_OPTION);
            } else {
                result.partitionOption = model.enablePartitionOption ? 'PartitionByKey' : 'None';
            }
            if (result.partitionOption !== 'None') {
                if (ValueAdapter.isTemplated(model, LakehouseWriteSettings.PARTITION_KEYS)) {
                    result.partitionNameList = ValueAdapter.toResource(model, LakehouseWriteSettings.PARTITION_KEYS);
                } else if (!arrayIsNullOrEmpty(model.partitionNameList)) {
                    result.partitionNameList = [];
                    model.partitionNameList.forEach((partitionName) => {
                        if (partitionName.partitionKey) {
                            (result.partitionNameList as string[]).push(partitionName.partitionKey);
                        }
                    });
                }
            }
        }
    }

    protected _lhTablePropsToModel(
        resource: Model.StoreWriteSettings,
        result: LakehouseWriteSettings,
        copySink: CopySink
    ): void {
        ValueAdapter.toModel(result, LakehouseWriteSettings.TABLE_ACTION, resource.tableActionOption);
        if (resource.tableActionOption !== 'Append') {
            if (resource.partitionOption == null || resource.partitionOption === 'None') {
                result.enablePartitionOption = false;
            } else if (resource.partitionOption === 'PartitionByKey') {
                result.enablePartitionOption = true;
            } else {
                ValueAdapter.toModel(result, LakehouseWriteSettings.PARTITION_OPTION, resource.partitionOption);
            }

            ValueAdapter.toModel(result, LakehouseWriteSettings.PARTITION_KEYS, resource.partitionNameList);
            if (Array.isArray(resource.partitionNameList) && !arrayIsNullOrEmpty(resource.partitionNameList)) {
                result.partitionNameList = [];
                (resource.partitionNameList as string[]).forEach((partitionName: string) => {
                    const modelPartitionColumn = Owned.create<StoreSettingPartitionKey>(
                        StoreSettingPartitionKey.MODEL,
                        result
                    );
                    modelPartitionColumn.partitionKey = partitionName;
                    result.partitionNameList.push(modelPartitionColumn);
                });
            }
        }
    }
}
